import { useEffect, useMemo, useState } from "react";
import { Box, Grid2, Link, Stack, Typography, useTheme } from "@mui/material";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";
import dayjs from "dayjs";

import { InvestigationButton } from "../components/InvestigationButton";
import {
  DragAndDropFiles,
  Separator,
  TemporalUploadedFile,
} from "../components/InvestigationComponents"; // Para archivos digitales
import { useTranslation } from "react-i18next";
import { RichTextEditor } from "../../../../MainComponents/RichTextEditor/RichTextEditor";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { createAttachment } from "../../../../../apis";
import { useConstants } from "../Main/useConstants";
import { useSelector } from "react-redux";
import { SectionedSelect } from "../../../../MainComponents/Select/SectionedSelect";
import { useReport } from "../../../../../context/report/ReportContext";

const evidenceSchema = {
  type: "", // Tipo de evidencia (Audio, Documento, etc.)
  type_details: "", // Detalles del tipo de evidencia
  upload_by: null, // Quién proporciona la evidencia
  upload_by_details: "", // Detalles de quién proporciona la evidencia
  kind: "", // Formato de evidencia (Digital, Física, etc.)
  description: "", // Descripción de la evidencia
  information: {
    details: "", // Detalles de cómo se obtuvo
    created_at: null, // Fecha de recepción
    deadline: "", // Vencimiento de la evidencia
    position: "", // Lugar de resguardo (solo si es física)
    compromised: false, // Evidencia comprometida
    compromised_details: "", // Detalles de la evidencia comprometida
    measures: false, // Acciones para preservar la evidencia
    measures_details: "", // Detalles de las acciones para preservar la evidencia
    fundamentals: "", // Fundamenta el reporte
    fundamentals_details: "", // Detalles de la fundamentación del reporte
    // Los siguientes campos se ignoran si no se usan:
    // budget, comment, measures, etc.
  },
  relationship: "",
  status: "", // Formato de evidencia (Digital, Física, etc.)
};

const validateForm = (evidence, files, currentFiles) => {
  const errors = {};

  if (!evidence.type) errors.type = true;
  if (!evidence.upload_by?.id) errors.upload_by = true;
  if (!evidence.kind) errors.kind = true;

  if (evidence.kind === "digital") {

    if (files.length === 0 && currentFiles.length === 0) errors.files = true;

  }

  if (evidence.kind === "fisic") {
    const text = evidence.description.replace(/<[^>]*>/g, "").trim();
    if (text.length === 0) {
      errors.description = true;
    }
    if (!evidence.information.position) errors.position = true;
  }

  return [Object.entries(errors).length === 0 ? true : false, errors];
};

export const AddEvidence = ({
  evidences,
  selectedEvidence,
  setSelectedEvidence,
  setIsEditing,
  handleSaveEvidenceDetails,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // Fuente de los usuarios para "Proporcionada por" (sesión, denunciante y asignados)
  const { state: reportState } = useReport();
  const { email, username } = useSelector((state) => state.auth);
  const {
    enums,
    evidences: { kindOpts, typeOpts, fundamentaOpts },
    selects: { typeUserLabels },
    functions: { getUserLabel },
  } = useConstants();

  const [evidence, setEvidence] = useState(_.cloneDeep(evidenceSchema));

  const [currentFiles, setCurrentFiles] = useState([]);
  const [files, setFiles] = useState([]);

  const [triedToSave, setTriedToSave] = useState(false);
  const [errors, setErrors] = useState({});

  const [isLoadingEvidenceForEdit, setIsLoadingEvidenceForEdit] =
    useState(true);

  const [addDetailsHowGet, setAddDetailsHowGet] = useState(false);
  const [addDetailsEvidence, setAddDetailsEvidence] = useState(false);

  const uploadByOpts = useMemo(() => {
    const {
      ME,
      COMPLAINANT,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_VICTIM,
      INVOLVEDS_WITNESS,
      RESOURCES,
      SYSTEM,
    } = enums.typeUser;
    const usersWanted = [
      ME,
      COMPLAINANT,
      INVOLVEDS_VICTIM,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_WITNESS,
      RESOURCES,
      SYSTEM,
    ];

    let newIndicatedOpts = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newIndicatedOpts.push({
          key: value,
          options: [
            {
              value: email,
              label: username || email || t("Evidences.assing_evidence_to_me"),
            },
          ],
        });
        return;
      }
      if (value === COMPLAINANT) {
        newIndicatedOpts.push({
          key: value,
          options: [
            {
              value: COMPLAINANT,
              label: t("Evidences.reporter"),
            },
          ],
        });
        return;
      }
      // Lista de cada tipo (víctimas, ofensores, etc.) según su ruta en reportState
      const path = _.get(enums, ["typeUserRoutes", value]);
      const node = _.get(reportState, path);
      const fromArray =
        (node &&
          node.formatted &&
          Array.isArray(node.formatted) &&
          node.formatted) ||
        (node && node.raw && Array.isArray(node.raw) && node.raw) ||
        (Array.isArray(node) && node) ||
        [];
      newIndicatedOpts.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    return newIndicatedOpts;
  }, [reportState, email, username]);

  // Cargar evidencia para editar
  useEffect(() => {
    if (selectedEvidence != null) {
      const currentEvidence = evidences[selectedEvidence];
      setEvidence(currentEvidence);
      setCurrentFiles(Array.isArray(currentEvidence.files) ? currentEvidence.files : []);
      setAddDetailsHowGet(!!currentEvidence.upload_by_details);
      setAddDetailsEvidence(!!currentEvidence.type_details);
    }
    setIsLoadingEvidenceForEdit(false);
  }, [selectedEvidence]);

  // Validar en cada cambio
  useEffect(() => {
    if (triedToSave) {
      const [, errors] = validateForm(evidence, files, currentFiles);
      setErrors(errors);
    }
  }, [evidence, files, currentFiles]);

  // Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setEvidence({ ...evidence, [name]: checked });
    } else {
      setEvidence({ ...evidence, [name]: value });
    }
  };

  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setEvidence({
      ...evidence,
      information: { ...evidence.information, [name]: value },
    });
  };

  const handleChangeFiles = (newFiles) => {
    setFiles([ ...files, ...newFiles ]);
  }

  const handleRemoveFile = (idx) => {
    const newFiles = [ ...files ];
    newFiles.splice(idx, 1);
    setFiles(newFiles);
  }

  const handleRemoveFileCurrent = (idx) => {
    const newFiles = [ ...currentFiles ];
    newFiles.splice(idx, 1);
    setCurrentFiles(newFiles);
  }

  const handleDateChange = (date, name) => {
    setEvidence({
      ...evidence,
      information: { ...evidence.information, [name]: date },
    });
  };

  const handleKindChange = (e) => {
    const { value } = e.target;
    setEvidence({
      ...evidence,
      kind: value,
      description: "",
      information: {
        ...evidence.information,
        created_at: null,
        deadline: null,
        position: "",
      },
    });
  };

  // Validación y guardado
  const handleSave = async () => {
    const [isValid, errors] = validateForm(evidence, files, currentFiles);
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }

    let newFiles = [];

    if (currentFiles.length > 0) {
      newFiles.push(...currentFiles);
    }

    if (evidence.kind === "digital" && files.length > 0) {
      const formData = new FormData();
      files.forEach(file => {
        formData.append("attachments", file);
      });
      try {
        const resp = await createAttachment(formData);
        const attachments_id = resp.attachments_id;
        const newTempFiles = attachments_id.map((id, idx) => ({
          file: id,
          name: files[idx].name,
          size: files[idx].size,
          type: files[idx].type,
        }));
        newFiles = [ ...newFiles, ...newTempFiles];
      } catch (error) {
        enqueueSnackbar({
          variant: "error",
          message: t("resources.missing_required_inputs"),
        });
        return;
      }
    }
    
    const newEvidences = _.cloneDeep(evidences || []);

    const objToSave = { 
      ...evidence,
      files: newFiles,
    };

    if (selectedEvidence == null) {
      newEvidences.push(objToSave);
    } else {
      newEvidences[selectedEvidence] = objToSave;
    }
    handleSaveEvidenceDetails(newEvidences);
  };

  const handleClose = () => {
    setSelectedEvidence(null);
    setIsEditing(false);
  };

  const returnIsValid = (error) => {
    if (!triedToSave) return null;
    if (error) return false;
    return true;
  };

  if (isLoadingEvidenceForEdit) return <></>;

  // Render
  return (
    <Box padding={2}>
      <Separator text={t("Evidences.add_new_evidence")} />

      <Grid2 container spacing={2} mt={2}>
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <SectionedSelect
            label={t("Evidences.evidence_provided_by")}
            value={evidence.upload_by?.id ?? ""}
            onChange={(op) =>
              handleChange({
                target: {
                  name: "upload_by",
                  value: { id: op.value, from: op.section, label: op.label },
                },
              })
            }
            sections={uploadByOpts}
            required={true}
            isValid={returnIsValid(errors?.upload_by) ?? null}
            showSuccessValidation
            showHelpText={false}
            fallbackLabel={getUserLabel(evidence.upload_by)}
          />
          {/* <EGBasicSelect
            label={t("Evidences.evidence_provided_by")}
            name="upload_by"
            value={evidence.upload_by}
            onChange={handleChange}
            options={usersOpts}
            required
            isValid={returnIsValid(errors?.upload_by) ?? null}
            showSuccessValidation
            showHelpText={false}
          /> */}
          {addDetailsHowGet ? (
            <Box mt={1.5}>
              <EGInput
                label={`${t("Evidences.add_details_how_obtained")}`}
                name="upload_by_details"
                value={evidence.upload_by_details}
                onChange={handleChange}
                type="textarea"
                multiline
                rows={5}
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="flex-end">
              <Link
                onClick={() => setAddDetailsHowGet(true)}
                underline="hover"
                mt={0.5}
              >
                + {t("Evidences.add_details_how_obtained")}
              </Link>
            </Box>
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("Evidences.evidence_type")}
            name="type"
            value={evidence.type}
            onChange={handleChange}
            options={typeOpts}
            required
            isValid={returnIsValid(errors?.type) ?? null}
            showSuccessValidation
            showHelpText={false}
          />
          {addDetailsEvidence ? (
            <Box mt={1.5}>
              <EGInput
                label={`${t("Evidences.add_details_of_evidence")}`}
                name="type_details"
                value={evidence.type_details}
                onChange={handleChange}
                type="textarea"
                multiline
                rows={5}
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="flex-end">
              <Link
                onClick={() => setAddDetailsEvidence(true)}
                underline="hover"
                mt={0.5}
              >
                + {t("Evidences.add_details_of_evidence")}
              </Link>
            </Box>
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 4 }}>
          <EGBasicSelect
            label={t("Evidences.evidence_format")}
            name="kind"
            value={evidence.kind}
            onChange={handleKindChange}
            options={kindOpts}
            required
            isValid={returnIsValid(errors?.kind) ?? null}
            showSuccessValidation
            showHelpText={false}
            disabled={!!evidences[selectedEvidence]?.kind}
          />
        </Grid2>

        {/* Campos dinámicos según formato */}
        {evidence.kind === "digital" && (
          <DigitalEvidence
            evidence={evidence}
            selectedEvidence={selectedEvidence}
            files={files}
            currentFiles={currentFiles}
            handleChangeFiles={handleChangeFiles}
            handleRemoveFile={handleRemoveFile}
            handleRemoveFileCurrent={handleRemoveFileCurrent}
            handleDateChange={handleDateChange}
            handleChange={handleChange}
            errors={errors}
            returnIsValid={returnIsValid}
          />
        )}

        {evidence.kind === "fisic" && (
          <FisicEvidence
            evidence={evidence}
            handleDateChange={handleDateChange}
            handleInfoChange={handleInfoChange}
            errors={errors}
            handleChange={handleChange}
            returnIsValid={returnIsValid}
          />
        )}
      </Grid2>

      <Grid2 container spacing={2} mt={2}>
        <Grid2 size={{ xs: 12, lg: 4 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              color={theme.palette.secondary.main}
            >
              {t("Evidences.compromised_evidence")}
            </Typography>
            <EGSwitch
              checked={evidence.information.compromised}
              onChange={() =>
                handleInfoChange({
                  target: {
                    name: "compromised",
                    value: !evidence.information.compromised,
                  },
                })
              }
              size="small"
              sx={{ marginRight: 0.5 }}
            />
          </Box>
          {evidence.information.compromised && (
            <Box mt={1.5}>
              <EGInput
                label={t("Evidences.details_of_compromised_evidence")}
                name="compromised_details"
                value={evidence.information.compromised_details}
                onChange={handleInfoChange}
                type="textarea"
                multiline
                rows={3}
                isValid={returnIsValid(errors?.compromised_details) ?? null}
                showHelpText={false}
              />
            </Box>
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 4 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              color={theme.palette.secondary.main}
            >
              {t("Evidences.actions_to_preserve_the_evidence")}
            </Typography>
            <EGSwitch
              checked={evidence.information.measures}
              onChange={() =>
                handleInfoChange({
                  target: {
                    name: "measures",
                    value: !evidence.information.measures,
                  },
                })
              }
              size="small"
              sx={{ marginRight: 0.5 }}
            />
          </Box>
          {evidence.information.measures && (
            <Box mt={1.5}>
              <EGInput
                label={t("Evidences.details_of_actions_to_preserve_the_evidence")}
                name="measures_details"
                value={evidence.information.measures_details}
                onChange={handleInfoChange}
                type="textarea"
                multiline
                rows={3}
                isValid={returnIsValid(errors?.measures_details) ?? null}
                showHelpText={false}
              />
            </Box>
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 4 }}>
          <EGBasicSelect
            label={t("Evidences.fundamenta_the_report")}
            name="fundamentals"
            value={evidence.information.fundamentals}
            onChange={handleInfoChange}
            options={fundamentaOpts}
            required
            isValid={returnIsValid(errors?.fundamentals) ?? null}
            showHelpText={false}
          />
          {evidence.information.fundamentals && (
            <Box mt={1.5}>
              <EGInput
                label={t("Evidences.details")}
                name="fundamentals_details"
                value={evidence.information.fundamentals_details}
                onChange={handleInfoChange}
                type="textarea"
                multiline
                rows={3}
                showHelpText={false}
              />
            </Box>
          )}
        </Grid2>
      </Grid2>

      <Stack spacing={2} mt={2}>
        {/* Botones */}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <InvestigationButton
            label={t("Evidences.cancel")}
            icon="close_icon"
            size="medium"
            onClick={handleClose}
          />
          <InvestigationButton
            label={t("Evidences.save")}
            variant="success"
            size="medium"
            icon="mark_check_bold"
            onClick={handleSave}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

const DigitalEvidence = ({
  evidence,
  selectedEvidence,
  files,
  currentFiles,
  handleChangeFiles,
  handleRemoveFile,
  handleRemoveFileCurrent,
  handleDateChange,
  handleChange,
  errors,
  returnIsValid,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();
  return (
    <>
      <Grid2 size={{ xs: 12, lg: 4 }}>
        <EGDatePicker
          label={t("Evidences.reception_date")}
          name="created_at"
          value={
            evidence.information.created_at
              ? dayjs(evidence.information.created_at)
              : null
          }
          onChange={(e) => handleDateChange(e, "created_at")}
          className="date-picker-deadline"
          format="DD/MMM/YYYY"
        />
      </Grid2>
      <Grid2 size={{ xs: 12, lg: 4 }}>
        <EGDatePicker
          label={t("Evidences.deadline")}
          name="deadline"
          value={
            evidence.information.deadline
              ? dayjs(evidence.information.deadline)
              : null
          }
          onChange={(e) => handleDateChange(e, "deadline")}
          className="date-picker-deadline"
          format="DD/MMM/YYYY"
        />
      </Grid2>
      <Grid2 size={{ xs: 12, lg: 8 }}>
        <DragAndDropFiles
          text={t("Evidences.drag_file")}
          onFileSelect={handleChangeFiles}
          multiple={true}
          isValid={returnIsValid(errors?.files) ?? null}
          // disabled={!!evidence.files?.length}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, lg: 4 }}>
        <Stack spacing={1}>
          {currentFiles.map((file, idx) => (
            <TemporalUploadedFile
              name={file?.name}
              size={file?.size}
              iconName="trash_can"
              iconColor={theme.palette.red.main}
              iconAction={() => handleRemoveFileCurrent(idx)}
              width="100%"
            />
          ))}
          {files.map((file, idx) => (
            <TemporalUploadedFile
              name={file?.name}
              size={file?.size}
              iconName="trash_can"
              iconColor={theme.palette.red.main}
              iconAction={() => handleRemoveFile(idx)}
                width="100%"
              />
          ))}
        </Stack>
      </Grid2>
      <Grid2 size={12}>
        <EGInput
          label={t("Evidences.evidence_description")}
          name="description"
          value={evidence.description}
          onChange={handleChange}
          type="textarea"
          multiline
          rows={3}
          showHelpText={false}
        />
      </Grid2>
    </>
  );
};

const FisicEvidence = ({
  evidence,
  handleDateChange,
  handleChange,
  handleInfoChange,
  errors,
  returnIsValid,
}) => {
  const [t] = useTranslation("investigation");
  return (
    <>
      <Grid2 size={{ xs: 12, lg: 4 }}>
        <EGDatePicker
          label={`${t("Evidences.reception_date")}`}
          name="created_at"
          value={
            evidence.information.created_at
              ? dayjs(evidence.information.created_at)
              : null
          }
          onChange={(e) => handleDateChange(e, "created_at")}
          className="date-picker-deadline"
          format="DD/MMM/YYYY"
        />
      </Grid2>
      <Grid2 size={12}>
        <RichTextEditor
          label={t("Evidences.evidence_description")}
          value={evidence.description}
          onChange={(value) =>
            handleChange({ target: { name: "description", value } })
          }
          isValid={returnIsValid(errors?.description) ?? null}
        />
      </Grid2>
      <Grid2 size={12}>
        <EGInput
          label={t("Evidences.position")}
          name="position"
          value={evidence.information.position}
          onChange={handleInfoChange}
          required
          isValid={returnIsValid(errors?.position) ?? null}
          showSuccessValidation
          showHelpText={false}
        />
      </Grid2>
    </>
  );
};
